import { RouterRootState } from 'connected-react-router';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGlobalState } from '../../../Providers';
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import ReportEditCard from '../../../components/report/ReportEditCard';
import { FIRST_IN_ROW_TYPE, GENERAL_MUSTERING_STATUS, LAST_IN_ROW_TYPE, ReportCategories, ReportSubTypes, ReportTypes } from '../../../constants/report-types';
import { SPECIFIC_HELP_KEY } from '../../../constants/specific-help-constant';
import { ReportCategoryName } from '../../../constants/system-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { MusteringReportTotals } from '../../../models/MusteringReportTotals';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { getAllReportNamesWithPagination, getReportById } from '../../../services/reportService';
import { showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import { getItemPath } from '../../../utilities/reportUtil';
import AbseenteeismCurrentWeekReport from './AbseenteeismCurrentWeekReport';
import AccessGroupReport from './AccessGroupReport';
import AlarmTransactions from './AlarmTransactions';
import AreaReport from './AreaReport';
import CardExpiryReport from './CardExpiryReport';
import DeniedTransactions from './DeniedTransactionReport';
import EnrolmentsReport from './EnrolmentsReport';
import FirstInLastOutReport from './FirstInLastOutReport';
import HardwareInstallationReport from './HardwareInstallationReport';
import HolidayReport from './HolidayReport';
import HoursWorkedReport from './HoursWorkedReport';
import MusteringReport from './MusteringReport';
import StatusTransactions from './StatusTransactions';
import CardsNotUsedReport from './CardsNotUsedReport';
import TimeAttendanceTransactionReport from './TimeAttendanceTransactionReport';
import Transactions from './Transactions';
import VingcardTransactionReport from './VingcardTransactionReport';
import ZoneOccupancyReport from './ZoneOccupancyReport';
import StillOnSite from './still-on-site';
import AuditReport from './AuditReport';
import DoorAccessReport from './DoorAccessReport';
import SequentialEventReport from './SequentialEventReport';
import PersonAccessReport from './PersonAccessReport';
import OperationalComplianceReport from './OperationalComplianceReport';
import GeneralAccessReport from "@/pages/access-control/reports/GeneralAccessReport.tsx";

const allItemsPath = '/access-control/reports';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface Props {
    reportCategoryName?: ReportCategoryName.TRANSACTIONAL | ReportCategoryName.TIME_AND_ATTENDANCE | ReportCategoryName.ACCESS_CONTROL | ReportCategoryName.SYSTEM_SETUP | ReportCategoryName.EMERGENCY_AND_SAFETY | ReportCategoryName.COMPLIANCE | ReportCategoryName.MY_REPORTS | "NONE";
}

const Report: FC<Props> = ({ reportCategoryName = "NONE" }) => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.report), badge: 0, showInitial: true, isActive: true }
    ];
    const initalTabContent: ContentWrapper[] = [{
        content: <>Report not found</>
    }]
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [content, setContent] = useState<any>(initalTabContent);
    const [tabs, setTabs] = useState(initialTabs);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [reportTypeId, setReportTypeId] = useState<string>("");
    const [musteringReportTotals, setMusteringReportTotals] = useState<MusteringReportTotals>();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const { setSpecificHelpTopicKey } = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const topic: Topic = {
        accessor: 'nameOriginal',
        allItemsPath,
        itemPath: getItemPath(reportCategoryName, true),
        title: translate.byKey(TranslationKeys.reports),
    }

    const reloadPage = async () => {
        try {
            setBusy(true);
            const report = await getReportById(itemId);
            setSelectedItem({label: report.name, value: report.id.toString()})
            setCurrentItem(report);
            setReportTypeId(report.typeId);
            setBreadcrumb([
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.reports) },
                { value: `${getItemPath(reportCategoryName, true)}${itemId}`, displayName: report.name },
            ]);
            setTabs(initialTabs);

            const reportContent = getReportContent(report.typeId, report.reportSubTypeId);
            setContent(reportContent.length > 0 ? reportContent : initalTabContent);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        } finally {
            setBusy(false);
        }
    }

    const getReportContent = (reportypeId: string, reportSubTypeId?: string) => {
        let content: ContentWrapper[] = [];
        if (reportypeId === ReportTypes.FIRST_IN_LAST_OUT) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.FIRST_IN_LAST_OUT_REPORT);
            content = [
                {
                    content: <FirstInLastOutReport reportId={itemId} reportType={FIRST_IN_ROW_TYPE}/>
                },
                {
                    content: <FirstInLastOutReport reportId={itemId} reportType={LAST_IN_ROW_TYPE}/>
                },
            ];

            const firstInLastOutTabs = [
                { label: translate.byKey(TranslationKeys.firstInReport), badge: 0, showInitial: true, isActive: true },
                { label: translate.byKey(TranslationKeys.lastOutReport), badge: 0, showInitial: true, isActive: true }
            ];

            setTabs(firstInLastOutTabs);
        }
        else if (reportypeId === ReportTypes.TRANSACTION && reportSubTypeId === ReportSubTypes.STATUS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.STATUS_TRANSACTIONS_REPORT);
            content = [
                {
                    content: <StatusTransactions reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.TRANSACTION && reportSubTypeId === ReportSubTypes.ALARMS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ALARM_TRANSACTIONS_REPORT);
            content = [
                {
                    content: <AlarmTransactions reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.TRANSACTION) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.TRANSACTION_REPORT);
            content = [
                {
                    content: <Transactions reportId={itemId} isTodayReportType={false} />
                }
            ];
        }
        if (reportypeId === ReportTypes.ABSENTEEISM && (reportSubTypeId === ReportSubTypes.TODAY || reportSubTypeId === ReportSubTypes.CURR_WEEK)) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ABSENTEEISM_REPORT);
            content = [
                {
                    content: <AbseenteeismCurrentWeekReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.ZONE_OCCUPANCY) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ZONE_OCCUPANCY_REPORT);
            content = [
                {
                    content: <ZoneOccupancyReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.HW_INSTALLATION) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.HARDWARE_INSTALLATION_REPORT);
            content = [
                {
                    content: <HardwareInstallationReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.CARD_EXPIRY) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.CARD_EXPIRY_REPORT);
            content = [
                {
                    content: <CardExpiryReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.HOLIDAYS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.HOLIDAY_REPORT);
            content = [
                {
                    content: <HolidayReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.AREAS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.AREA_REPORT);
            content = [
                {
                    content: <AreaReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.ENROLMENTS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ENROLMENT_REPORT);
            content = [
                {
                    content: <EnrolmentsReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.ACCESS_GROUPS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ACCESS_GROUP_REPORT);
            content = [
                {
                    content: <AccessGroupReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.AUDIT) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.AUDIT_REPORT);
            content = [
                {
                    content: <AuditReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.HOURS_WORKED) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.HOURS_WORKED_REPORT);
            content = [
                {
                    content: <HoursWorkedReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.TRANSACTION && reportSubTypeId === ReportSubTypes.DENIED) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.DENIED_TRANSACTIONS_REPORT);
            content = [
                {
                    content: <DeniedTransactions reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.MUSTERING) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.GENERAL_MUSTERING_REPORT);
            content = [
                {
                    content: <MusteringReport key={"all"} reportId={itemId} reportStatus={GENERAL_MUSTERING_STATUS.ALL} setMusteringReportTotals={setMusteringReportTotals} />
                },
                {
                    content: <MusteringReport key={"unaccounted_for"} reportId={itemId} reportStatus={GENERAL_MUSTERING_STATUS.UNACCOUNTED_FOR} setMusteringReportTotals={setMusteringReportTotals} />
                },
                {
                    content: <MusteringReport key={"exited"} reportId={itemId} reportStatus={GENERAL_MUSTERING_STATUS.EXITED} setMusteringReportTotals={setMusteringReportTotals} />
                },
                {
                    content: <MusteringReport key={"absent"} reportId={itemId} reportStatus={GENERAL_MUSTERING_STATUS.ABSENT} setMusteringReportTotals={setMusteringReportTotals} />
                },
                {
                    content: <MusteringReport key={"mustered"} reportId={itemId} reportStatus={GENERAL_MUSTERING_STATUS.MUSTERED} setMusteringReportTotals={setMusteringReportTotals} />
                },
            ];

            const musteringTabs = [
                { label: translate.byKey(TranslationKeys.all), badge: 0, showInitial: true, isActive: true },
                { label: translate.byKey(TranslationKeys.unaccountedFor), badge: 0, showInitial: true, isActive: true },
                { label: translate.byKey(TranslationKeys.exited), badge: 0, showInitial: true, isActive: true },
                { label: translate.byKey(TranslationKeys.absent), badge: 0, showInitial: true, isActive: true },
                { label: translate.byKey(TranslationKeys.mustered), badge: 0, showInitial: true, isActive: true },
            ];

            setTabs(musteringTabs);
        }
        else if (reportypeId === ReportTypes.TRANSACTION && reportSubTypeId === ReportSubTypes.TIME_ATTENDANCE) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.TIME_AND_ATTENDANCE_REPORT);
            content = [
                {
                    content: <TimeAttendanceTransactionReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.TRANSACTION && reportSubTypeId === ReportSubTypes.VINGCARD) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.VINGCARD_TRANSACTION_REPORT);
            content = [
                {
                    content: <VingcardTransactionReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.OCCUPANCY) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.STILL_ON_SITE_REPORT);
            content = [
                {
                    content: <StillOnSite reportId={itemId} />
                }
            ]
        }
        else if (reportypeId === ReportTypes.CARDS_NOT_USED) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.CARDS_NOT_USED_REPORT);
            content = [
                {
                    content: <CardsNotUsedReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.DOOR_ACCESS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.DOOR_ACCESS);
            content = [
                {
                    content: <DoorAccessReport reportId={itemId} />
                }
            ]
        }
        else if (reportypeId === ReportTypes.SEQUENTIAL_EVENT) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.ENTRY_TEST_OUTCOMES_REPORT);
            content = [
                {
                    content: <SequentialEventReport reportId={itemId} />
                }
            ]
        }
        else if (reportypeId === ReportTypes.PERSON_ACCESS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.PERSON_ACCESS);
            content = [
                {
                    content: <PersonAccessReport reportId = {itemId} />
                }
            ]
        }
        else if (reportypeId === ReportTypes.OPERATIONAL_COMPLIANCE) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.OPERATIONAL_COMPLIANCE);
            content = [
                {
                    content: <OperationalComplianceReport reportId={itemId} />
                }
            ];
        }
        else if (reportypeId === ReportTypes.GENERAL_ACCESS) {
            setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.GENERAL_ACCESS);
            content = [
                {
                    content: <GeneralAccessReport reportId={itemId} />
                }
            ]
        }

        return content;
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = content[index ?? 0];
        return selectedTabContent.content;
    };

    const getDataMethod = (listParameters) => {
        switch (reportCategoryName) {
            case ReportCategoryName.TRANSACTIONAL:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.TRANSACTION], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.TIME_AND_ATTENDANCE:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.TIME_AND_ATTENDANCE], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.ACCESS_CONTROL:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.ACCESS_CONTROL], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.SYSTEM_SETUP:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.SYSTEM_SETUP], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.EMERGENCY_AND_SAFETY:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.EMERGENCY_AND_SAFETY], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.COMPLIANCE:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter([ReportCategories.COMPLIANCE], 'Report category', 'category', listParameters?.filters ?? [])})
            case ReportCategoryName.MY_REPORTS:
                return getAllReportNamesWithPagination({ ...listParameters, filters: appendFilter(user.id, "Created By Current User", "userLogin.id", [], "=in=") });
            default:
                return getAllReportNamesWithPagination(listParameters, user.profileId);
        }
    }

    return <TopicContextProvider
        dataMethod={(listParameters) => getDataMethod(listParameters)}
        itemId={itemId}
        topic={topic}
        selectedItem={selectedItem}>
        <ItemContentTabTwo className={"overflow-x-auto"}>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={() => { }}
                tabs={tabs}
                initialValues={currentItem}
                disableSaveButton={true}
                children={
                    <ReportEditCard
                        isGeneralMusteringReport={reportTypeId === ReportTypes.MUSTERING}
                        dataTotals={musteringReportTotals} />
                } />
        </ItemContentTabTwo>
    </TopicContextProvider>
}
export default Report;
